import type { ColumnDef } from '@tanstack/react-table'
import {
  ChevronDown,
  CreditCard,
  Eye,
  FileText,
  Key,
  Package,
  RefreshCw,
  RotateCcw,
  Settings,
  User,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { eventsService } from '../services/eventsService'
import type {
  ActivityEvent,
  ActivityFilters,
  ActivityQuery,
} from '../types/events.types'
import { SignalCommandSearch } from './SignalCommandSearch'

// Mock pagination functions - you'll need to implement these with your actual API
const useMockPagination = () => {
  const [isLoading, _setIsLoading] = useState(false)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(true)

  const fetchNextPage = async () => {
    setIsFetchingNextPage(true)
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsFetchingNextPage(false)
    setHasNextPage(false)
  }

  return { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage }
}

const getEntityIcon = (entity: string) => {
  switch (entity.toLowerCase()) {
    case 'plan':
      return <Package className='w-4 h-4' />
    case 'contract':
      return <FileText className='w-4 h-4' />
    case 'api_key':
      return <Key className='w-4 h-4' />
    case 'payment':
      return <CreditCard className='w-4 h-4' />
    case 'user':
      return <User className='w-4 h-4' />
    case 'customer':
      return <User className='w-4 h-4' />
    case 'invoice':
      return <FileText className='w-4 h-4' />
    default:
      return <Settings className='w-4 h-4' />
  }
}

const getEventColor = (eventName: string) => {
  if (eventName.includes('created')) return 'bg-green-100 text-green-800'
  if (eventName.includes('updated')) return 'bg-blue-100 text-blue-800'
  if (eventName.includes('deleted')) return 'bg-red-100 text-red-800'
  if (eventName.includes('generated')) return 'bg-purple-100 text-purple-800'
  return 'bg-gray-100 text-gray-800'
}

export function ActivityTab() {
  const [activity, setActivity] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedActivity, setSelectedActivity] =
    useState<ActivityEvent | null>(null)
  const [filters, setFilters] = useState<ActivityFilters>({})
  const [fromDate, setFromDate] = useState<Date | undefined>()
  const [toDate, setToDate] = useState<Date | undefined>()
  const [fromDateOpen, setFromDateOpen] = useState(false)
  const [toDateOpen, setToDateOpen] = useState(false)
  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useMockPagination()

  // Mock event name options for autocomplete
  const eventNameOptions = [
    {
      value: 'user.created',
      label: 'user.created',
      description: 'User account created',
    },
    {
      value: 'user.updated',
      label: 'user.updated',
      description: 'User profile updated',
    },
    {
      value: 'user.deleted',
      label: 'user.deleted',
      description: 'User account deleted',
    },
    {
      value: 'plan.created',
      label: 'plan.created',
      description: 'Subscription plan created',
    },
    {
      value: 'plan.updated',
      label: 'plan.updated',
      description: 'Subscription plan updated',
    },
    {
      value: 'contract.created',
      label: 'contract.created',
      description: 'Contract created',
    },
    {
      value: 'contract.updated',
      label: 'contract.updated',
      description: 'Contract updated',
    },
    {
      value: 'api_key.generated',
      label: 'api_key.generated',
      description: 'API key generated',
    },
    {
      value: 'payment.processed',
      label: 'payment.processed',
      description: 'Payment processed',
    },
    {
      value: 'invoice.created',
      label: 'invoice.created',
      description: 'Invoice created',
    },
  ]

  // Mock user options for autocomplete
  const userOptions = [
    {
      value: 'john.doe@company.com',
      label: 'john.doe@company.com',
      description: 'John Doe - Admin',
    },
    {
      value: 'jane.smith@company.com',
      label: 'jane.smith@company.com',
      description: 'Jane Smith - Developer',
    },
    {
      value: 'mike.wilson@company.com',
      label: 'mike.wilson@company.com',
      description: 'Mike Wilson - Manager',
    },
    {
      value: 'sarah.johnson@company.com',
      label: 'sarah.johnson@company.com',
      description: 'Sarah Johnson - User',
    },
    {
      value: 'david.brown@company.com',
      label: 'david.brown@company.com',
      description: 'David Brown - Support',
    },
    {
      value: 'lisa.davis@company.com',
      label: 'lisa.davis@company.com',
      description: 'Lisa Davis - Analyst',
    },
    {
      value: 'system@company.com',
      label: 'system@company.com',
      description: 'System User - Automated',
    },
  ]

  const loadActivity = useCallback(async () => {
    setLoading(true)
    try {
      const query: ActivityQuery = {
        filters,
        limit: 50,
        sort: 'timestamp',
        order: 'desc',
      }
      const response = (await (
        eventsService as unknown as Record<
          string,
          (...args: unknown[]) => unknown
        >
      ).getActivity(query)) as { data: typeof activity }
      setActivity(response.data)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  const handleFilterChange = (field: keyof ActivityFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }))
  }

  const clearFilters = () => {
    setFilters({})
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const formatTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDateTime(dateString)
  }

  const hasActiveFilters = () => {
    return (
      filters.event_name ||
      filters.entity ||
      filters.actor ||
      filters.from_date ||
      filters.to_date
    )
  }

  const columns: ColumnDef<ActivityEvent>[] = [
    {
      accessorKey: 'timestamp',
      header: 'Time',
      cell: ({ row }) => {
        const activity = row.original
        return (
          <div className='text-sm'>
            <div className='font-medium'>
              {formatDateTime(activity.timestamp)}
            </div>
            <div className='text-xs text-muted-foreground'>
              {formatTimeAgo(activity.timestamp)}
            </div>
          </div>
        )
      },
      size: 400,
    },
    {
      accessorKey: 'event_name',
      header: 'Event Name',
      cell: ({ row }) => {
        const activity = row.original
        return (
          <div className='text-sm'>
            <span className='font-medium'>{activity.event_name}</span>
          </div>
        )
      },
      size: 400,
    },
    {
      accessorKey: 'entity',
      header: 'Entity',
      cell: ({ row }) => {
        const activity = row.original
        return (
          <div className='text-sm'>
            <div className='flex items-center gap-2'>
              {getEntityIcon(activity.entity)}
              <div>
                <div className='font-medium'>{activity.entity}</div>
                <div className='text-xs text-muted-foreground font-mono'>
                  {activity.entity_id}
                </div>
              </div>
            </div>
          </div>
        )
      },
      size: 390,
    },
    {
      accessorKey: 'actor',
      header: 'Actor',
      cell: ({ row }) => {
        const activity = row.original
        return (
          <div className='text-sm'>
            <div className='flex items-center gap-2'>
              <User className='w-4 h-4 text-muted-foreground' />
              <div>
                <div className='font-medium'>{activity.actor}</div>
                <div className='text-xs text-muted-foreground font-mono'>
                  {activity.actor_id}
                </div>
              </div>
            </div>
          </div>
        )
      },
      size: 340,
    },
  ]

  return (
    <div className='space-y-6'>
      {/* Filters */}
      <div className='space-y-4'>
        <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
          <div className='space-y-2'>
            <Label>Event Name</Label>
            <SignalCommandSearch
              value={filters.event_name}
              onValueChange={value => handleFilterChange('event_name', value)}
              options={eventNameOptions}
              placeholder='Select an event'
              className='w-full'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='entity-filter'>Entity</Label>
            <Select
              value={filters.entity || 'all'}
              onValueChange={value =>
                handleFilterChange('entity', value === 'all' ? '' : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='All entities' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All entities</SelectItem>
                <SelectItem value='plan'>Plan</SelectItem>
                <SelectItem value='contract'>Contract</SelectItem>
                <SelectItem value='api_key'>API Key</SelectItem>
                <SelectItem value='payment'>Payment</SelectItem>
                <SelectItem value='user'>User</SelectItem>
                <SelectItem value='customer'>Customer</SelectItem>
                <SelectItem value='invoice'>Invoice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label>User</Label>
            <SignalCommandSearch
              value={filters.actor}
              onValueChange={value => handleFilterChange('actor', value)}
              options={userOptions}
              placeholder='Select a user'
              className='w-full'
            />
          </div>

          <div className='space-y-2'>
            <Label>From Date</Label>
            <Popover open={fromDateOpen} onOpenChange={setFromDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full justify-between font-normal'
                >
                  {fromDate ? fromDate.toLocaleDateString() : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <Calendar
                  mode='single'
                  selected={fromDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setFromDate(date)
                    setFromDateOpen(false)
                    handleFilterChange(
                      'from_date',
                      date ? date.toISOString() : ''
                    )
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className='space-y-2'>
            <Label>To Date</Label>
            <Popover open={toDateOpen} onOpenChange={setToDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full justify-between font-normal'
                >
                  {toDate ? toDate.toLocaleDateString() : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <Calendar
                  mode='single'
                  selected={toDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setToDate(date)
                    setToDateOpen(false)
                    handleFilterChange(
                      'to_date',
                      date ? date.toISOString() : ''
                    )
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {hasActiveFilters() && (
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <span>Active filters:</span>
              {filters.event_name && (
                <Badge variant='secondary'>Event Name</Badge>
              )}
              {filters.entity && <Badge variant='secondary'>Entity</Badge>}
              {filters.actor && <Badge variant='secondary'>Actor</Badge>}
              {filters.from_date && (
                <Badge variant='secondary'>From Date</Badge>
              )}
              {filters.to_date && <Badge variant='secondary'>To Date</Badge>}
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={clearFilters}
              className='flex items-center gap-2'
            >
              <RotateCcw className='w-4 h-4' />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className='flex justify-between items-center'>
        <div className='text-sm text-muted-foreground'>
          {activity.length} activity event{activity.length !== 1 ? 's' : ''}{' '}
          found
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={loadActivity}
          disabled={loading}
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <VirtualizedTable
          data={activity}
          columns={columns}
          isLoading={isLoading || loading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          containerHeight='calc(100vh - 480px)'
          emptyMessage='No activity events found. Events will appear here when users make changes on the platform.'
          loadingMessage='Loading more activity...'
          onRowClick={activity => setSelectedActivity(activity)}
          enableSorting={true}
          enableColumnResizing={true}
          enableColumnReordering={true}
          enableColumnPinning={false}
        />
      </div>

      {/* Activity Detail Dialog */}
      {selectedActivity && (
        <Dialog
          open={!!selectedActivity}
          onOpenChange={() => setSelectedActivity(null)}
        >
          <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <Eye className='w-5 h-5' />
                Activity Details
              </DialogTitle>
              <DialogDescription>
                {selectedActivity.event_name} •{' '}
                {formatDateTime(selectedActivity.timestamp)}
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-6 py-4'>
              {/* Summary */}
              <div className='grid gap-4 md:grid-cols-2'>
                <div className='space-y-3'>
                  <div>
                    <div className='text-sm font-medium'>Event Name</div>
                    <Badge
                      className={`mt-1 ${getEventColor(selectedActivity.event_name)}`}
                    >
                      {selectedActivity.event_name}
                    </Badge>
                  </div>

                  <div>
                    <div className='text-sm font-medium'>Entity</div>
                    <div className='flex items-center gap-2 mt-1'>
                      {getEntityIcon(selectedActivity.entity)}
                      <div>
                        <div className='text-sm font-medium'>
                          {selectedActivity.entity}
                        </div>
                        <div className='text-xs text-muted-foreground font-mono'>
                          {selectedActivity.entity_id}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className='text-sm font-medium'>Actor</div>
                    <div className='flex items-center gap-2 mt-1'>
                      <User className='w-4 h-4 text-muted-foreground' />
                      <div>
                        <div className='text-sm font-medium'>
                          {selectedActivity.actor}
                        </div>
                        <div className='text-xs text-muted-foreground font-mono'>
                          {selectedActivity.actor_id}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='space-y-3'>
                  <div>
                    <div className='text-sm font-medium'>Timestamp</div>
                    <div className='text-sm text-muted-foreground mt-1'>
                      {formatDateTime(selectedActivity.timestamp)}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {formatTimeAgo(selectedActivity.timestamp)}
                    </div>
                  </div>

                  <div>
                    <div className='text-sm font-medium'>Activity ID</div>
                    <div className='text-sm text-muted-foreground mt-1 font-mono'>
                      {selectedActivity.id}
                    </div>
                  </div>

                  <div>
                    <div className='text-sm font-medium'>Created</div>
                    <div className='text-sm text-muted-foreground mt-1'>
                      {formatDateTime(selectedActivity.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              {selectedActivity.metadata &&
                Object.keys(selectedActivity.metadata).length > 0 && (
                  <div>
                    <div className='text-sm font-medium mb-2'>Metadata</div>
                    <pre className='bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-sm font-mono overflow-auto'>
                      {JSON.stringify(selectedActivity.metadata, null, 2)}
                    </pre>
                  </div>
                )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
